<template>
  <base-card :loading="pending" tabled>
    <template v-if="investors">
      <base-table>
        <thead>
          <tr>
            <th>Investor</th>
            <th>Total Invested</th>
            <th>Total Owned</th>
            <th>Total Accrued</th>
            <th>Number of Shares</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(investor, key) in investors" :key="key">
            <td>
              {{ investor.user.profile.firstName }} {{ investor.user.profile.lastName }}
              <br>
              {{ investor.user.email }}
            </td>
            <td>{{ $formatting.toCurrency(investor.invested) }}</td>
            <td>{{ $formatting.toCurrency(investor.shares * investment.costPerShare) }}</td>
            <td>{{ $formatting.toCurrency(investor.accrued) }}</td>
            <td>{{ investor.shares }}</td>
            <td class="text-right">
              <div class="btn-group">
                <base-button
                  small
                  @click="pay(key)"
                >
                  Repayment
                </base-button>
                <base-button
                  :loading="isDownloadInvestorTransactionsPending && downloadingTransactionsFor === investor.user._id"
                  small
                  @click="downloadTransactions(investor.user)"
                >
                  Transactions
                </base-button>
                <base-button
                  :loading="sharesPending && user === investor.user._id"
                  secondary
                  small
                  @click="breakdown(investor.user)"
                >
                  Breakdown
                </base-button>
              </div>
            </td>
          </tr>
        </tbody>
      </base-table>
    </template>
    <template v-else>
      <p class="text-center">
        There are no investors yet.
      </p>
    </template>
    <template v-if="investors && pages > 1" v-slot:body-bottom>
      <base-pagination :current="page" :total="pages" @go="request" />
    </template>
    <base-modal large target="breakdown" title="Share Breakdown">
      <template v-for="(share, index) in shares">
        <div :key="share._id" class="mb-4">
          <base-table bordered small>
            <colgroup>
              <col width="22%" />
              <col width="22%" />
              <col width="22%" />
              <col width="22%" />
              <col width="12%" />
            </colgroup>
            <thead>
              <tr>
                <th colspan="5">{{ share.funding.name }}</th>
              </tr>
              <tr>
                <th>Last Activity Date</th>
                <th>Owned Shares</th>
                <th>Total Value</th>
                <th>Total Accrued</th>
                <th>Days</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(audit, row) in getBuyerAudit(share)" :key="audit._id">
                <td class="align-top">
                  {{ $dates.toDate(audit.startAt || audit.createdAt) }} to {{ $moment().format('DD/MM/YYYY') }}
                </td>
                <td class="text-right align-bottom">
                  <div v-if="row !== 0" :class="{ 'text-success': hasPurchasedShares(index, row), 'text-danger': hasSoldShares(index, row) }">
                    {{ getTotalSharesDiff(index, row) }}
                  </div>
                  {{ audit.totalShares }}
                </td>
                <td class="text-right align-bottom">
                  <div v-if="row !== 0" :class="{ 'text-success': hasPurchasedShares(index, row), 'text-danger': hasSoldShares(index, row) }">
                    {{ getTotalCostDiff(index, row) }}
                  </div>
                  {{ $formatting.toCurrency(audit.totalCost) }}
                </td>
                <td class="text-right align-bottom">
                  {{ $formatting.toCurrency(audit.totalAccrued) }}
                </td>
                <td class="text-right align-bottom">
                  {{ audit.totalDaysAccruedFor }}
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td></td>
                <td class="text-right">
                  <strong>{{ getFinalRow(index).totalShares }}</strong>
                </td>
                <td class="text-right">
                  <strong>{{ $formatting.toCurrency(getFinalRow(index).totalCost) }}</strong>
                </td>
                <td class="text-right">
                  <strong>{{ $formatting.toCurrency(getFinalRow(index).totalAccrued) }}</strong>
                </td>
                <td class="text-right">
                  <strong>{{ getTotalDays(index) }}</strong>
                </td>
              </tr>
            </tfoot>
          </base-table>
        </div>
      </template>
      <base-table bordered small>
        <colgroup>
          <col width="22%" />
          <col width="22%" />
          <col width="22%" />
          <col width="22%" />
          <col width="12%" />
        </colgroup>
        <thead>
          <tr>
            <th colspan="5">Total position as at {{ $moment().format('DD/MM/YYYY') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td></td>
            <td class="text-right">
              <strong>{{ getTotalShares() }}</strong>
            </td>
            <td class="text-right">
              <strong>{{ getTotalCost() }}</strong>
            </td>
            <td class="text-right">
              <strong>{{ getTotalAccrued() }}</strong>
            </td>
            <td></td>
          </tr>
        </tbody>
      </base-table>
      <template v-slot:footer>
        <base-button :loading="isDownloadBreakdownPending" @click="downloadBreakdownAsXLSX">
          Download for Excel
        </base-button>
      </template>
    </base-modal>
    <investor-repayment :investment="investment" :user.sync="payment" />
  </base-card>
</template>

<script>
import BigNumber from 'bignumber.js'
import { mapActions, mapState } from 'vuex'

import InvestorRepayment from '@/components/InvestorRepayment.vue'

export default {
  name: 'InvestmentInvestors',

  components: {
    InvestorRepayment
  },

  data: () => ({
    downloadingTransactionsFor: null,
    page: 1,
    payment: {},
    shares: [],
    user: null
  }),

  created () {
    this.request()
  },

  computed: {
    ...mapState({
      isDownloadBreakdownPending: store => store.core.shares.isDownloadBreakdownPending,
      isDownloadInvestorTransactionsPending: store => store.core.investments.isDownloadInvestorTransactionsPending,
      investment: store => store.core.investments.entity,
      investors: store => store.core.userInvestments.rows,
      pages: store => store.core.userInvestments.totalPages,
      pending: store => store.core.userInvestments.isListPending,
      sharesPending: store => store.core.shares.isTrailPending
    })
  },

  methods: {
    breakdown (user) {
      this.user = user._id

      this.getTrail({
        investment: this.investment._id,
        user: user._id
      })
        .then((shares) => {
          this.shares = this.$_.sortBy(shares, ['funding.name'])
          this.$modal.show('#breakdown')
        })
    },

    downloadBreakdownAsXLSX () {
      this.downloadBreakdown({
        investment: this.investment._id,
        user: this.user
      })
        .then((download) => {
          window.location = this.$document.path(download)
        })
    },

    async downloadTransactions (user) {
      this.downloadingTransactionsFor = user._id

      const document = await this.downloadInvestorTransactions({
        investment: this.investment._id,
        user: user._id
      })

      window.location = this.$document.path(document)
    },

    getBuyerAudit (share) {
      return share.audit.filter((audit) => {
        return audit.buyer._id === share.user._id
      })
    },

    getCurrentValue (index, row, value) {
      return this.$_.get(this.shares[index].audit[row], value)
    },

    getFinalRow (index) {
      return this.shares[index].audit[this.shares[index].audit.length - 1]
    },

    getPreviousValue (index, row, value) {
      return this.$_.get(this.shares[index].audit[row - 1], value)
    },

    getTotalAccrued () {
      let total = new BigNumber(0)

      this.$_.map(this.shares, 'totalAccrued').forEach((value) => {
        total = total.plus(value)
      })

      return this.$formatting.toCurrency(total.toNumber())
    },

    getTotalCost () {
      let total = new BigNumber(0)

      this.$_.map(this.shares, 'totalValue').forEach((value) => {
        total = total.plus(value)
      })

      return this.$formatting.toCurrency(total.toNumber())
    },

    getTotalCostDiff (index, row) {
      const previous = this.getPreviousValue(index, row, 'totalCost')
      const current = this.getCurrentValue(index, row, 'totalCost')

      if (previous > current) {
        return `- ${this.$formatting.toCurrency(previous - current)}`
      } else if (previous < current) {
        return `+ ${this.$formatting.toCurrency(current - previous)}`
      }
    },

    getTotalDays (index) {
      return this.$_.sumBy(this.getBuyerAudit(this.shares[index]), 'totalDaysAccruedFor')
    },

    getTotalShares () {
      return this.$_.sumBy(this.shares, 'totalShares')
    },

    getTotalSharesDiff (index, row) {
      const previous = this.getPreviousValue(index, row, 'totalShares')
      const current = this.getCurrentValue(index, row, 'totalShares')

      if (previous > current) {
        return `- ${previous - current}`
      } else if (previous < current) {
        return `+ ${current - previous}`
      }
    },

    hasPurchasedShares (index, row) {
      const previous = this.getPreviousValue(index, row, 'totalShares')
      const current = this.getCurrentValue(index, row, 'totalShares')

      return previous < current
    },

    hasSoldShares (index, row) {
      const previous = this.getPreviousValue(index, row, 'totalShares')
      const current = this.getCurrentValue(index, row, 'totalShares')

      return previous > current
    },

    pay (key) {
      this.payment = this.investors[key].user
      this.$modal.show('#investorRepaymentModal')
    },

    request (page) {
      if (page) {
        this.page = page
      }

      this.getInvestors({
        page: this.page,
        populate: ['user'],
        query: {
          investment: this.investment._id
        }
      })
    },

    ...mapActions({
      downloadBreakdown: 'core/shares/download-breakdown',
      downloadInvestorTransactions: 'core/investments/download-investor-transactions',
      getInvestors: 'core/user-investments/list',
      getTrail: 'core/shares/trail'
    })
  }
}
</script>
